import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import translate from '../../libs/i18n';
import roundToPrecision from '../../utils/roundToPrecision';
import { examPerformanceNumbersShape } from '../../utils/propTypesShapes';

import { ExamResumeChartStyle } from './ExamResumeChartStyle';

const ExamResumeChart = ({ radiographies, title, tomographies }) => {
  const language = useSelector((state) => state.settings.language);

  const approved = useMemo(() => {
    let current = 0;

    if (tomographies?.all?.approved) {
      current += tomographies?.all?.approved;
    }

    if (radiographies?.all?.approved) {
      current += radiographies?.all?.approved;
    }

    return current;
  }, [radiographies, tomographies]);

  const reproved = useMemo(() => {
    let current = 0;

    if (tomographies?.all?.reproved) {
      current += tomographies?.all?.reproved;
    }

    if (radiographies?.all?.reproved) {
      current += radiographies?.all?.reproved;
    }

    return current;
  }, [radiographies, tomographies]);

  const total = useMemo(() => {
    let current = 0;

    if (approved) {
      current += approved;
    }

    if (reproved) {
      current += reproved;
    }

    return current;
  }, [approved, reproved]);

  const approvedPercent = useMemo(() => {
    let current = 0;

    if (total > 0) {
      current = approved / total;
    }

    return current;
  }, [approved, total]);

  const reprovedPercent = useMemo(() => {
    let current = 0;

    if (total > 0) {
      current = reproved / total;
    }

    return current;
  }, [reproved, total]);

  return (
    <ExamResumeChartStyle>
      {title?.length ? (
        <div className="exam-resume-chart-title">{title}</div>
      ) : null}
      <div className="exam-resume-chart-container">
        {total > 0 ? (
          <>
            {approvedPercent && approvedPercent > 0 ? (
              <div className="chart-approved-wrapper">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 150 150">
                  <circle cx="75" cy="75" r="75" />
                </svg>
              </div>
            ) : null}
            {reprovedPercent && reprovedPercent > 0 ? (
              <div className="chart-reviewed-wrapper">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="-1 -1 2 2">
                  <path
                    d={`M 1 0 A 1 1 0 ${
                      reprovedPercent > 0.5 ? 1 : 0
                    } 1 ${Math.cos(2 * Math.PI * reprovedPercent)} ${Math.sin(
                      2 * Math.PI * reprovedPercent
                    )} L 0 0`}
                  />
                </svg>
              </div>
            ) : null}
            <div className="chart-role-wrapper">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 94 94">
                <circle cx="47" cy="47" r="47" />
              </svg>
            </div>
            {approvedPercent && approvedPercent > 0 ? (
              <div
                className={`chart-label chart-label-approved${
                  approvedPercent <= 0.375 ? ' revert-position' : ''
                }`}
              >
                <span className="label-text">
                  {translate('approved', language)}
                </span>
                <span className="label-value">{`${roundToPrecision(
                  approvedPercent * 100,
                  0
                )}%`}</span>
                <span className="label-quantity">{approved} {translate(approved > 1 ? 'exams' : 'exam', language)}
                </span>
              </div>
            ) : null}
            {reprovedPercent && reprovedPercent > 0 ? (
              <div
                className={`chart-label chart-label-reviewed${
                  reprovedPercent >= 0.625 ? ' revert-position' : ''
                }`}
              >
                <span className="label-text">{translate('rev', language)}</span>
                <span className="label-value">{`${roundToPrecision(
                  reprovedPercent * 100,
                  0
                )}%`}</span>
                <span className="label-quantity">{reproved} {translate(reproved > 1 ? 'exams' : 'exam', language)}</span>
              </div>
            ) : null}
          </>
        ) : (
          <>
            <div className="chart-empty-wrapper">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 150 150">
                <circle cx="75" cy="75" r="75" />
              </svg>
            </div>
            <div className="chart-role-wrapper">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 94 94">
                <circle cx="47" cy="47" r="47" />
              </svg>
            </div>
          </>
        )}
      </div>
    </ExamResumeChartStyle>
  );
};

ExamResumeChart.propTypes = {
  radiographies: PropTypes.oneOfType([
    PropTypes.oneOf([null]),
    examPerformanceNumbersShape,
  ]),
  title: PropTypes.string,
  tomographies: PropTypes.oneOfType([
    PropTypes.oneOf([null]),
    examPerformanceNumbersShape,
  ]),
};

ExamResumeChart.defaultProps = {
  radiographies: null,
  title: '',
  tomographies: null,
};

export default ExamResumeChart;
